import { SelectedItemsObj } from '@components/multi-selector/types';

/**
 * Canonical role_scope values from the API.
 * Use RoleScope.OWNER (etc.) in checks — do not hardcode strings.
 * Legacy RoleType / OrgRoleType enums keep the same string values for compat.
 */
export const RoleScope = {
  OWNER: 'owner',
  ORGANISATION_ADMIN: 'organisation_admin',
  BRANCH_ADMIN: 'branch_admin',
  GROUP_ADMIN: 'group_admin',
  FINANCE: 'finance',
  INDIVIDUAL: 'individual',
  SUPER_ADMIN: 'super_admin',
  OPERATOR: 'operator',
  BANK_ADMIN: 'bank_admin',
  ATTENDANCE_MANAGER: 'attendance_manager',
  EVENT_MANAGER: 'event_manager',
  PAYMENT_MANAGER: 'payment_manager',
  EVENT_VOLUNTEER: 'event_volunteer',
  SALES_MANAGER: 'sales_manager',
  SALES_AGENT: 'sales_agent'
} as const;

// Const object + type share a name so callers use RoleScope.OWNER and RoleScope as a type.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RoleScope = (typeof RoleScope)[keyof typeof RoleScope];

export type RoleScopeUiVariant =
  | 'org_info'
  | 'branch_multi_select'
  | 'group_picker'
  | 'none';

/** Access choice for custom organisation_admin roles on invite/edit. */
export type OrgAccessMode = 'all_branches' | 'specific_groups';

export const PermissionRuleType = {
  OR: 'OR',
  HIDE: 'HIDE',
  MANDATORY: 'MANDATORY',
  DEPENDENCY_AND: 'DEPENDENCY_AND',
  DEPENDENCY_OR: 'DEPENDENCY_OR'
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PermissionRuleType =
  (typeof PermissionRuleType)[keyof typeof PermissionRuleType];

export interface PermissionRule {
  permissions: string[];
  rule: PermissionRuleType;
  /**
   * Required for DEPENDENCY_AND / DEPENDENCY_OR.
   * If any of `permissions` is selected:
   * - DEPENDENCY_AND — all targetPermissions must be selected
   * - DEPENDENCY_OR — at least one targetPermission must be selected
   */
  targetPermissions?: string[];
}

export interface OrgRoleMeta {
  id: number;
  name: string;
  displayName?: string;
  role_scope: RoleScope;
  permissions: string[];
  isCustom?: boolean;
  creationPermission?: string;
  /**
   * API permission_rules:
   * MANDATORY — locked when creating a custom role
   * HIDE — hidden in UI (still sent on create when assignable)
   * OR — single permission: locked; multiple: at least one must stay selected
   * DEPENDENCY_AND — each selected child requires all targetPermissions
   * DEPENDENCY_OR — each selected child requires at least one targetPermission
   */
  permissionRules?: PermissionRule[];
  /** When true, current user can clone/create a custom role from this role. */
  allowExtendingRole?: boolean;
}

export interface GetCustomRolesResponse {
  roles: OrgRoleMeta[];
  currentUserPermissions: string[];
  canCreateCustomRoles: boolean;
}

export interface CreateCustomRoleRequest {
  base_role_id: number;
  name: string;
  permissions: string[];
}

export interface CreateCustomRoleResponse {
  id: number;
  name: string;
  display_name?: string;
  role_scope: RoleScope;
  isCustomRole: boolean;
  permissions: string[];
}

export interface CreateCustomRoleFormState {
  roleName: string;
  baseRoleId: number | null;
  selectedPermissions: string[];
  roleNameError?: string;
  /** Inline error above Create footer (e.g. Quick Collect member deps). */
  permissionSelectionError?: string;
}

export type MemberModalView =
  | 'form'
  | 'create-custom-role'
  | 'view-permissions'
  | 'select-groups';

export interface MemberFormState {
  name: string;
  phoneNumber: string;
  selectedRole: OrgRoleMeta | null;
  branchIds: string[];
  groupsByBranch: Record<string, string[]>;
  selectedGroups: SelectedItemsObj;
  /** Used when selected role is a custom organisation_admin role. */
  orgAccessMode: OrgAccessMode;
}

export interface InviteMemberBody {
  mobile: string;
  name: string;
  role: string;
  is_custom_role?: boolean;
  branches?: InviteBranchPayload[] | InviteGroupPayload[];
}

export interface UpdateMemberRoleBody {
  user_id: string;
  role_name: string;
  is_custom_role?: boolean;
  branches?: InviteBranchPayload[] | InviteGroupPayload[];
}

export interface EditTeamMemberNameBody {
  name: string;
}

export interface InviteBranchPayload {
  branch_id: string;
}

export interface InviteGroupPayload {
  branch_id: string;
  group_ids: string[];
}

export interface InviteMemberModalProps {
  isOpen: boolean;
  isEdit: boolean;
  userOrgRoleId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export interface MemberFormProps {
  isEdit: boolean;
  /** Selected role was created in this modal session, so access must be chosen. */
  isSelectedRoleNewlyCreated?: boolean;
  formState: MemberFormState;
  roles: OrgRoleMeta[];
  currentUserPermissions: string[];
  canCreateCustomRoles: boolean;
  onChange: (patch: Partial<MemberFormState>) => void;
  onOpenCreateRole: () => void;
  onOpenViewPermissions: () => void;
  onOpenSelectGroups: () => void;
}

export interface UseMemberModalParams {
  isOpen: boolean;
  isEdit: boolean;
  userOrgRoleId?: string;
  organizationId: string;
  roles: OrgRoleMeta[];
  onClose: () => void;
  onSuccess: () => void;
}
